<template>
  <div class="grid" :style="{ ...contentItemsStyle, ...style }" v-on="$listeners">
    <!-- @slot Default slot for grid items -->
    <slot />
  </div>
</template>

<script>
/**
 * This is a component for using CSS grid
 */
export default {
  name: 'Grid',

  props: {
    /**
     *  CSS grid gap property
     */
    gap: String,
    /**
     *  CSS grid grid-template-columns property
     */
    gtc: String,
    /**
     *  CSS grid grid-template-rows property
     */
    gtr: String,
    /**
     *  CSS grid grid-template-areas property
     */
    gta: String,
    /**
     *  CSS grid grid-auto-flow property
     */
    gaf: String,
    /**
     *  CSS grid align-items property
     */
    alignItems: String,
    /**
     *  CSS grid align-content property
     */
    alignContent: String,
    /**
     *  CSS grid justify-items property
     */
    justifyItems: String,
    /**
     *  CSS grid justify-content property
     */
    justifyContent: String,

    /**
     * CSS grid place-items property
     */
    placeItems: String,

    /**
     * CSS grid place-content property
     */
    placeContent: String,

    /**
     * Shorthand for using justify-content and align-items
     */
    contentItems: String
  },

  computed: {
    contentItemsStyle() {
      return {
        ...(!!this.contentItems && { 'justify-content': this.contentItems, 'align-items': this.contentItems })
      }
    },

    style() {
      return {
        gap: this.gap,
        ...(this.gtc && { 'grid-template-columns': this.gtc }),
        ...(this.gtr && { 'grid-template-rows': this.gtr }),
        ...(this.gta && { 'grid-template-areas': this.gta }),
        ...(this.gaf && { 'grid-auto-flow': this.gaf }),
        ...(this.placeItems && { 'place-items': this.placeItems }),
        ...(this.placeContent && { 'place-content': this.placeContent }),
        ...(this.alignItems && { 'align-items': this.alignItems }),
        ...(this.alignContent && { 'align-content': this.alignContent }),
        ...(this.justifyItems && { 'justify-items': this.justifyItems }),
        ...(this.justifyContent && { 'justify-content': this.justifyContent })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
}
</style>

<docs>
  ### Example 1

  Below we have 4 spans.

  ```jsx
    <span>Grid item 1</span>
    <span>Grid item 2</span>
    <span>Grid item 3</span>
    <span>Grid item 4</span>
  ```

  If we want to display Grid them, we simply wrap them with the `Grid` component:

  ```jsx
    <Grid>
      <span>Grid item 1</span>
      <span>Grid item 2</span>
      <span>Grid item 3</span>
      <span>Grid item 4</span>
    </Grid>
  ```
  ---------------------


  ### Example 2

  Now let's use the `Grid` component to build a layout with columns and rows

  ```jsx
  <Grid gtc="80px 1fr" gtr="40px 80px">
    <span style="border: 1px solid">Grid item 1</span>
    <span style="border: 1px solid">Grid item 2</span>
    <span style="border: 1px solid">Grid item 3</span>
    <span style="border: 1px solid">Grid item 4</span>
  </Grid>
  ```
</docs>
